import { number2kanji } from '@geolonia/japanese-numeral';
import { isHiragana, isKatakana, isRomaji, toHiragana, toKatakana } from 'wanakana';

import Language from 'types/LanguageEnum';
import { Dictionaries } from 'models/Dictionaries';
import { CandidateProvider } from 'components/Keyboard/modules/candidates';

import { Candidate, JMDict } from '../types';

const numbersToKanji: CandidateProvider = async (input: string) => {
  try {
    const asKanji = number2kanji(parseInt(input, 10));
    return asKanji ? [asKanji] : [];
  } catch {
    return [];
  }
};

const romajiToKana = (input: string) => {
  try {
    if (isRomaji(input)) {
      return [toHiragana(input), toKatakana(input)];
    }
  } catch {}
  return [];
};

const kanaToKanji = async (inputKana: string, maxResults: number): Promise<Candidate[]> => {
  const jmdict = Dictionaries.get(Language.JA_JP) as JMDict;
  const results: Candidate[] = [];
  if (jmdict) {
    for (const { kana = [], kanji = [] } of jmdict.words) {
      for (const matchingKana of kana) {
        if (matchingKana.text.startsWith(inputKana)) {
          const kanjiList = kanji.length > 0 ? kanji : [{ text: matchingKana.text }];
          for (const { text } of kanjiList) {
            results.push({ kanji: text, kana: matchingKana.text });
            if (results.length >= maxResults) {
              return results;
            }
          }
        }
      }
    }
  }

  return results;
};

const groupCandidates = (suggestions: Candidate[]): string[] => {
  const candidates = new Set<string>();
  suggestions.forEach(({ kana, kanji }) => {
    if (kana) {
      candidates.add(kana);
    }
    if (kanji) {
      candidates.add(kanji);
    }
  });

  return Array.from(candidates);
};

const addCandidate = (candidates: string[], input: string, candidate: string) => {
  if (candidate && candidate !== input) {
    candidates.push(candidate);
  }
};

const DIGIT_KANJI: Record<string, string> = {
  '0': '〇', '1': '一', '2': '二', '3': '三', '4': '四',
  '5': '五', '6': '六', '7': '七', '8': '八', '9': '九',
};

const numbersToDigitWiseKanji = (input: string): string => {
  return input.split('').map(char => DIGIT_KANJI[char]).join('');
};

const getCandidates: CandidateProvider = async (input, maxResults = 20) => {
  let candidates: string[] = [];

  if (!Number.isNaN(Number(input)) && input.trim() !== '') {
    candidates.push(input);
    candidates.push(...(await numbersToKanji(input)));
    candidates.push(numbersToDigitWiseKanji(input));
    return candidates;
  }

  // Romaji => Hiragana + Katakana + suggestions for it
  if (isRomaji(input)) {
    const [hiragana, katakana] = romajiToKana(input);
    addCandidate(candidates, input, hiragana);
    addCandidate(candidates, input, katakana);

    if (hiragana) {
      const hiraganaCandidates = await kanaToKanji(hiragana, 10);
      candidates.push(...groupCandidates(hiraganaCandidates));
    }
    if (katakana) {
      const katakanaCandidates = await kanaToKanji(katakana, 10);
      candidates.push(...groupCandidates(katakanaCandidates));
    }
  }

  // Hiragana => input first, then Katakana + suggestions
  if (isHiragana(input)) {
    candidates.push(input);
    const katakana = toKatakana(input);
    addCandidate(candidates, input, katakana);

    const suggestions = await kanaToKanji(input, maxResults);
    candidates.push(...groupCandidates(suggestions));
  }

  // Katakana => input first, then Hiragana + suggestions
  if (isKatakana(input)) {
    candidates.push(input);
    const hiragana = toHiragana(input);
    addCandidate(candidates, input, hiragana);

    const suggestions = await kanaToKanji(hiragana, maxResults);
    candidates.push(...groupCandidates(suggestions));
  }

  return candidates.filter(c => c !== input);
};

export default getCandidates;
